import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { RouterLink } from '@angular/router';

import { AuthModalService } from '../../shared/services/auth-modal.service';
import { Facebook, Instagram, LucideIconData, MessageCircle, Music2 } from 'lucide-angular';

import { toSignal } from '@angular/core/rxjs-interop';
import { catchError, of } from 'rxjs';

import { AosDirective } from '../../shared/directives/aos.directive';
import { CountUpDirective } from '../../shared/directives/count-up.directive';
import { IconComponent } from '../../shared/ui';
import { NewsletterFormComponent } from '../../shared/ui/newsletter-form/newsletter-form.component';
import { EventCardComponent } from '../events/components/event-card/event-card.component';
import { EVENTS_COPY, UPCOMING_EVENTS_LIMITS } from '../events/constants/events.constants';
import { EventsService } from '../events/data/events.service';
import type { StoreEvent } from '../events/data/events.model';
import { PlayerFinderComponent } from './components/player-finder/player-finder.component';
import { MOCK_COMMUNITY } from './data/mock-community';

@Component({
  selector: 'app-community-page',
  imports: [
    IconComponent,
    AosDirective,
    CountUpDirective,
    NewsletterFormComponent,
    EventCardComponent,
    PlayerFinderComponent,
    RouterLink,
  ],
  templateUrl: './community-page.component.html',
  styleUrl: './community-page.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block' },
})
export class CommunityPageComponent {
  private readonly authModal = inject(AuthModalService);
  private readonly eventsService = inject(EventsService);

  protected readonly data = MOCK_COMMUNITY;
  protected readonly eventsCopy = EVENTS_COPY;

  /**
   * Próximos eventos de toda la tienda. Degradación por consumidor, como en el
   * resto del repo: un fallo se rotula como "todavía no hay", que es lo que el
   * usuario puede entender, y nunca deja la página a medias.
   */
  private readonly upcoming = toSignal(
    this.eventsService.getUpcoming().pipe(
      catchError((err) => {
        console.error('[community] failed to load upcoming events', err);
        return of<StoreEvent[]>([]);
      }),
    ),
    { initialValue: [] as StoreEvent[] },
  );

  /**
   * Corte de `/comunidad` sobre la respuesta canónica compartida (D10). Cuatro:
   * es una tira, y es lo único real de esta página —el resto sigue siendo
   * contenido de muestra—, así que merece más presencia que la portada; pero
   * tampoco es la agenda, que vive en `/eventos`.
   */
  protected readonly events = computed(() =>
    this.upcoming().slice(0, UPCOMING_EVENTS_LIMITS.community),
  );

  protected readonly mascotError = signal(false);

  protected onMascotError(): void {
    this.mascotError.set(true);
  }

  /**
   * El corgi del estado vacío tiene su propio flag: si su imagen falla, el
   * copy y el enlace deben seguir ahí. Compartir flag con el del hero haría
   * que un 404 en una imagen se llevara la otra.
   */
  protected readonly eventsMascotError = signal(false);

  protected onEventsMascotError(): void {
    this.eventsMascotError.set(true);
  }

  protected openRegister(): void {
    this.authModal.open('register');
  }

  protected readonly whatsappIcon: LucideIconData = MessageCircle;
  protected readonly instagramIcon: LucideIconData = Instagram;
  protected readonly facebookIcon: LucideIconData = Facebook;
  protected readonly tiktokIcon: LucideIconData = Music2;

  protected readonly channelIcons: Record<string, LucideIconData> = {
    instagram: Instagram,
    facebook: Facebook,
    tiktok: Music2,
    whatsapp: MessageCircle,
  };
}
